/**
 * Height as feet + inches.
 *
 * The single "num" box this replaces asked for height with a numeric keypad, so the
 * placeholder ("e.g. 5 ft 10") was literally untypeable on a phone — there is no
 * apostrophe, no space, no letters on that keypad. People entered what they could,
 * and the stored answers show exactly that: "54", "510", "5.3", "5,5", and 157
 * people who typed just "5" and lost their inches entirely.
 *
 * Two boxes fix it at the source: each field genuinely IS a number, so the numeric
 * keypad becomes correct rather than a trap, and "5 feet 4 inches" can't collapse
 * into the ambiguous "54".
 */

/** Inches must be 0-11; feet is bounded to keep a typo out of the record. */
const MAX_FEET = 8;
const MAX_INCHES = 11;

/**
 * Canonical stored form: `5' 10"`, or `5'` when inches are unknown.
 *
 * Nothing downstream parses height — the admin portal and the duplicate-merge dialog
 * only ever print it, and both Profile.height and the HubSpot NTQ property are plain
 * strings — so this is chosen to read well to a coach rather than to satisfy a
 * parser. It deliberately does not copy the legacy `5"6 "` shape, which is a
 * mis-rendered artifact rather than a format worth spreading.
 */
export function formatHeight(feet, inches) {
  const f = String(feet ?? '').trim();
  const i = String(inches ?? '').trim();
  if (!f && !i) return '';
  if (!f) return ''; // inches alone is not a height anyone means
  return i === '' ? `${f}'` : `${f}' ${i}"`;
}

/**
 * Best-effort read of an already-stored height back into the two boxes.
 *
 * Needed for resume: someone who answered before this change comes back to a screen
 * that now has two fields, and dropping their answer on the floor would be worse
 * than showing them our best reading of it. They see the result in the boxes and can
 * correct it — which makes this screen the right place to disambiguate values that
 * are genuinely ambiguous ("64" is either 6'4" or 64 inches, and only the person
 * whose height it is can say which).
 *
 * Returns { feet, inches } as strings, '' where unknown.
 */
export function parseHeight(value) {
  const empty = { feet: '', inches: '' };
  if (value === null || value === undefined) return empty;

  const raw = String(value).trim();
  if (!raw) return empty;

  // Any separator at all — ' " . , space, "ft", "in" — means the person distinguished
  // the two parts, so trust the grouping. Covers the canonical 5' 10", the legacy
  // 5"6 ", and the 5.3 / 5,5 people who reached for a decimal point.
  const groups = raw.match(/\d+/g);
  if (!groups || groups.length === 0) return empty;

  const separated = /[^0-9]/.test(raw.replace(/^\D+|\D+$/g, '')) || groups.length > 1;

  let feet;
  let inches;

  if (separated && groups.length > 1) {
    [feet, inches] = groups;
  } else {
    // A bare run of digits. One digit is feet with the inches lost ("5"). Two or
    // three digits is almost always feet-then-inches, because that is what the
    // old placeholder asked for — but only accept it when the tail is a real
    // inches value, so "231" doesn't become 2 feet 31 inches.
    const digits = groups[0];
    if (digits.length === 1) {
      feet = digits;
    } else {
      const tail = digits.slice(1);
      if (Number(tail) <= MAX_INCHES) {
        feet = digits[0];
        inches = tail;
      }
      // Tail isn't a possible inches value ("180" from someone answering in cm), so
      // the whole reading is suspect — including its first digit. Leave both boxes
      // empty rather than show a confident "1 foot" that was never said.
    }
  }

  const f = Number(feet);
  const i = inches === undefined ? NaN : Number(inches);

  return {
    feet: Number.isFinite(f) && f >= 1 && f <= MAX_FEET ? String(f) : '',
    inches: Number.isFinite(i) && i >= 0 && i <= MAX_INCHES ? String(i) : '',
  };
}

/**
 * The next value for one box, given what it held and what the field now reads.
 *
 * Ignoring an over-range keystroke — the obvious reading of "keep a typo out of the
 * record" — makes the box impossible to CORRECT, which is worse than the typo it
 * prevents. A filled box puts the caret after the digit, so typing appends: Feet 5
 * plus a 6 reads "56", every appended digit is over the max, and every one is
 * dropped. Feet can never leave its first answer (any two digits beat 8) and neither
 * can a two-digit Inches (any three beat 11). It looks frozen rather than refusing,
 * because nothing on screen changes — reported from the field as "you have to the
 * first time click your 5'10, if you try to edit it, you can't go past it".
 *
 * So an over-range result is read as a fresh answer rather than an addition: keep the
 * digit just typed and drop what was there. 5 then 6 is 6 feet; 10 then 1 is 1 inch,
 * and a second 1 makes the 11 they were reaching for. Restarting can only ever be a
 * digit the person actually pressed, and it stays on screen for them to see and fix —
 * a value that silently refuses to change does not.
 *
 * A digit that is out of range on its own (9 feet) still leaves the box alone: there
 * is no reading of it to keep.
 *
 * NOTE for anyone tempted by maxLength as a tidier guard — it re-creates this bug
 * exactly. The browser drops the keystroke before onChange, so the box goes back to
 * being uneditable while full.
 */
export function nextPart(prev, raw, max) {
  const digits = String(raw).replace(/\D/g, '');
  if (digits === '') return '';
  if (Number(digits) <= max) return String(Number(digits));

  const typed = digits.slice(-1);
  return Number(typed) <= max ? typed : String(prev ?? '');
}

export { MAX_FEET, MAX_INCHES };
